import { useEffect, useMemo } from 'react';

import {
  bugoAgencyDeliveryPopulate,
  listBugoAgencyDeliveries,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { listGiftRebateSettlements } from '@shared/api/giftRebateSettlement/giftRebateSettlement.controller';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { useAsyncFn } from 'react-use';

import { ROLE } from '../utils/rebate-settlement.utils';

// ─────────────────────────────────────────────
// useAgencyGiftSettlementData
// ─────────────────────────────────────────────

/** `useAgencyGiftSettlementData`가 반환하는 값의 타입 */
export interface AgencyGiftSettlementData {
  /** 정산 완료 내역 목록 (이 중 Draft를 정산 예정으로 표시) */
  historyItems: GiftRebateSettlement[] | undefined;
  /** 정산 완료 내역 로딩 중 여부 */
  historyLoading: boolean;
  /** 배송완료(미정산) 내역 목록 */
  deliveredEntries: GiftDeliveryForAdmin[] | undefined;
  /** 배송완료 내역 로딩 중 여부 */
  deliveredLoading: boolean;
}

/**
 * 상조 전용(리베이트) 정산 페이지에 필요한 서버 데이터를 페치해 반환하는 커스텀 훅.
 *
 * - 정산 완료 내역(`listGiftRebateSettlements`): 이 중 Draft가 정산 예정으로 표시된다.
 * - 배송완료 미정산 내역(`listBugoAgencyDeliveries`): 배송완료 & rebate 미정산 건.
 * 마운트 시 병렬로 요청한다.
 */
export function useAgencyGiftSettlementData(): AgencyGiftSettlementData {
  const [{ loading: historyLoading, value: historyDocs }, fetchHistory] = useAsyncFn(
    async () =>
      // lines.giftDelivery 를 populate 해야 정산 예정/완료 테이블의 배송 메타
      // (구매자/고인명/지도사/배송일)와 취소 여부(deliveryStatus)를 알 수 있다.
      listGiftRebateSettlements(
        {},
        [{ path: 'lines.giftDelivery', populate: bugoAgencyDeliveryPopulate }],
        { sort: { year: -1, month: -1 } },
        ROLE,
      ),
    [],
  );

  const [{ loading: deliveredLoading, value: deliveredEntries }, fetchDelivered] =
    useAsyncFn(
      async () =>
        listBugoAgencyDeliveries(
          // 배송완료 & 아직 리베이트 정산되지 않은(rebateSettlement=null) 건만 조회
          { deliveryStatus: GiftDeliveryStatus.Delivered, rebateSettlement: null },
          { 'shipping.deliveredAt': -1 },
        ),
      [],
    );

  useEffect(() => {
    fetchHistory();
    fetchDelivered();
  }, [fetchHistory, fetchDelivered]);

  // 취소(Canceled)된 배송 라인은 정산 합계/건수에서 제외한다 (공급사 정산과 동일 정책).
  // 표준 흐름상 배송완료(Delivered) 건은 취소되지 않지만, 운영자 수동 정정 등
  // 예외로 라인에 취소건이 섞일 수 있어 방어적으로 거른다.
  // populate 안 된(문자열) 라인은 상태를 알 수 없으니 그대로 둔다.
  const historyItems = useMemo<GiftRebateSettlement[] | undefined>(() => {
    if (!historyDocs) return undefined;
    return historyDocs.map((doc) => ({
      ...doc,
      lines: doc.lines.filter((line) => {
        const d = line.giftDelivery;
        return !(
          typeof d === 'object' && d?.deliveryStatus === GiftDeliveryStatus.Canceled
        );
      }),
    }));
  }, [historyDocs]);

  return {
    historyItems,
    historyLoading,
    deliveredEntries,
    deliveredLoading,
  };
}
